'use client';

import { AnimatePresence, motion, Variants } from 'framer-motion';
import { ArrowRight, Search, X } from 'lucide-react';
import { FormEvent, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { useHeaderStore } from '@lib/stores/useHeaderStore';
import { useClickAway } from '@lib/hooks/useClickAway';
import { Input } from '@components/common/input';
import { Label } from '@components/common/label';
import { useResize } from '@lib/hooks/useResize';
import { Routes } from '@lib/routes';

const inputVariants: Variants = {
  'collapsed': {
    opacity: 0,
    width: 0,
  },
  'desktop-expanded': {
    opacity: 1,
    width: '150%',
  },
  'mobile-expanded': {
    opacity: 1,
    width: '97%',
  },
};

export function LowerHeaderCollapsibleSearch() {
  const formRef = useRef<HTMLFormElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const router = useRouter();
  const { searchExpanded, setSearchExpanded } = useHeaderStore();
  const { width } = useResize();

  useClickAway({ ref: formRef, onClickAway: () => setSearchExpanded(false) });

  const handleIconClick = () => {
    const event = new Event('submit', { bubbles: true });
    formRef.current?.dispatchEvent(event);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (searchTerm.length > 2) {
      router.push(`${Routes.collection}?globalFilter=${searchTerm}`);
    }
  };

  const Icon = searchExpanded ? ArrowRight : Search;
  const label = searchExpanded ? 'Expanded Search' : 'Collapsed Search';

  const lg = width >= 1024;
  const expanded = lg ? 'desktop-expanded' : 'mobile-expanded';

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      className='relative flex items-center flex-1 flex-shrink-0'
    >
      <AnimatePresence>
        {searchExpanded && (
          <motion.div
            variants={inputVariants}
            initial='collapsed'
            exit='collapsed'
            animate={searchExpanded ? expanded : 'collapsed'}
            transition={{ duration: 0.2 }}
            className='absolute right-8 flex items-center max-w-[calc(100%_-_20px)] xl:max-w-none'
          >
            <span className='hidden xl:inline absolute -left-full size-full bg-gradient-to-r from-transparent to-primary' />
            <div className='w-full flex items-center relative z-10'>
              <Input
                id='search'
                className='px-10 peer !border-primary-foreground/20 focus:!border-primary/50 focus:!ring-primary-foreground'
                placeholder='Search...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Label
                htmlFor='search'
                className='absolute left-2 text-primary/50 peer-focus:text-primary/80 hover:text-primary xl:hover:text-primary/60'
                onClick={handleIconClick}
              >
                <Search aria-hidden='true' className='size-6' />
                <span className='sr-only'>Search</span>
              </Label>
              {searchTerm.length > 0 && (
                <button
                  type='button'
                  className='absolute right-2 text-primary/50 peer-focus:text-primary/60 hover:text-primary xl:hover:text-primary/60'
                  onClick={() => setSearchTerm('')}
                >
                  <X aria-hidden='true' className='size-6' />
                  <span className='sr-only'>Clear</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        className='absolute right-0 text-primary-foreground/80 hover:text-primary-foreground'
        onClick={() => setSearchExpanded(!searchExpanded)}
      >
        <Icon aria-hidden='true' className='size-6' />
        <span className='sr-only'>{label}</span>
      </button>
    </form>
  );
}
